import React, { useEffect, useState } from 'react'
import Text from 'components/_UI/Text';
import styled from 'styled-components';
import colors from 'theme/colors';
import { useEvent } from 'context/events/Event';
import BasicInfos from './parts/BasicInfos';
import CategoriesInfo from './parts/CategoriesInfo';
import AddressInfo from './parts/AddressInfo';
import ExtraInfo from './parts/ExtraInfo';
import { useForm } from 'react-hook-form';
import eventsApi from 'api/requests/events';
import dayjs from 'dayjs';
import date from 'helpers/date';
import ImageInfo from './parts/ImageInfo';
import buttonText from 'texts/buttons/buttonText';
import { useUser } from 'context/user/User';
import tagsApi from 'api/requests/tags';
import { useTags } from 'context/events/Tags';
import imageUpload from 'api/requests/imageUpload';
import breakpoints from 'theme/breakpoints';
import validateInputFile from 'helpers/validate-input-file';
import allowedFilesTypes from 'texts/messages/allowedFilesTypes';
import { AlertCircle, ArrowRight } from 'lucide-react';


export default function EventForm({
    edit,
    isDraft,
    setCreateDraftClick,
    onLoading = () => false,
    onSuccess = () => false,
    onError = () => false,
}) {
    const { event, setEvent } = useEvent();
    const { user } = useUser();
    const { tags, setTags } = useTags();

    const [trigger, setTrigger] = useState(false);
    const [tagsError, setTagsError] = useState(false);
    const [errorImage, setErrorImage] = useState(false);
    const [requestError, setRequestError] = useState(false);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedTags, setSelectedTags] = useState(false);
    const [errorTypeImage, setErrorTypeImage] = useState(false)

    const [imageId, setImageId] = useState(null);
    const [isUploadImage, setIsUploadImage] = useState(false);
    const [thumb, setThumb] = useState(null);
    const [descriptionError, setDescriptionError] = useState(false);
    const [description, setDescription] = useState(null);
    const [descriptionWithHtml, setDescriptionWithHtml] = useState(null);
    const [errorTypeImageDescription, setErrorTypeImageDescription] = useState(null)
    const [pathImageDescription, setPathImageDescription] = useState(null)
    const [spotifyPlaylistUrl, setSpotifyPlaylistUrl] = useState(null)


    const {
        register,
        unregister,
        handleSubmit,
        reset,
        setValue,
        getValues,
        trigger: formTrigger,
        formState: { errors }
    } = useForm();

    useEffect(() => {
        if (edit) {
            setEvent(edit);
            setImageId(edit?.image_id)
            setThumb(edit?.thumb)
            setDescription(edit?.description || null)
            setDescriptionWithHtml(edit?.description_with_html || null)
            setSpotifyPlaylistUrl(edit?.spotify_playlist_url || null)

            const defaultData = {
                name: edit?.name || '',
                ...datesToEdit(edit?.dates) || ''
            }

            reset(defaultData)

            Object.keys(defaultData).forEach((name) => {
                formTrigger(name);
            });
        }

        getCategories()
    }, [edit])


    async function getCategories() {
        if (tags) {
            return
        }
        setTagsLoading(true);
        try {
            const { data } = await tagsApi.get()
            setTags(data)
        } catch (error) {
            console.log('error')
        } finally {
            setTagsLoading(false);
        }
    }

    function datesToEdit(data) {
        const transformedResponse = {};

        Object.keys(data).forEach(key => {
            const item = data[key];
            const index = Number(key) + 1;

            transformedResponse[`date${index}`] = date.format(item.date);
            transformedResponse[`start${index}`] = item.start.substring(0, 5);
            transformedResponse[`end${index}`] = item.end.substring(0, 5);
        });

        return transformedResponse;
    }


    function removeDuplicates(obj) {
        const result = obj;

        for (const key in result) {
            if (key.startsWith('start') || key.startsWith('end') || key.startsWith('date')) {
                delete result[key];
            }
        }

        return result;
    }

    function createDatesPayload(content) {
        const result = [];
        const regex = /^date\d+$/;
        let count = 0;

        for (const key in content) {
            if (regex.test(key)) {
                count++;
            }
        }

        for (let i = 1; i <= count; i++) {
            const dateKey = "date" + i;
            const startKey = "start" + i;
            const endKey = "end" + i;

            if (content[dateKey] && content[startKey] && content[endKey]) {
                const fomratedStartTime = content[startKey].split(':').length > 2 ? content[startKey] : `${content[startKey]}:00`
                const fomratedEndTime = content[endKey].split(':').length > 2 ? content[endKey] : `${content[endKey]}:00`

                const startDate = date.unformat(content[dateKey])

                const [startH, startM] = content[startKey].split(':').map(Number)
                const [endH, endM] = content[endKey].split(':').map(Number)
                const isOvernight = (endH * 60 + endM) < (startH * 60 + startM)

                if (isOvernight) {
                    const nextDate = dayjs(startDate).add(1, 'day').format('YYYY-MM-DD')
                    result.push({
                        date: startDate,
                        start: fomratedStartTime,
                        end: '23:59:00'
                    });
                    result.push({
                        date: nextDate,
                        start: '00:00:00',
                        end: fomratedEndTime
                    });
                } else {
                    result.push({
                        date: startDate,
                        start: fomratedStartTime,
                        end: fomratedEndTime
                    });
                }
            }
        }

        return result;
    }

    async function onToggleDelete() {
        setErrorImage(false)
        setErrorTypeImage(false)
        const id = imageId

        try {
            const { data } = await imageUpload.delete(id)
            setImageId(null)
            setEvent({ ...event, images_id: [] })
        } catch (error) {
            console.log(error);
        }
    }


    async function uploadImage(file) {

        setIsUploadImage(true)

        const validationFile = validateInputFile(file);

        if (!validationFile.isValid) {
            setErrorTypeImage(validationFile.message);
            setIsUploadImage(false);
            return
        }

        const formData = new FormData()
        formData.append('image', file)
        formData.append('folder', 'events')

        try {
            const { data } = await imageUpload.uploadWithFolder(formData)

            setImageId(data?.id)
            setEvent({ ...event, images_id: [data.id], images: data?.thumb })
            setThumb(data?.thumb)


        } catch (error) {
            setErrorImage(true)
        } finally {
            setIsUploadImage(false)
        }
    }


    async function uploadImageDescription(file) {

        setIsUploadImage(true)

        const validationFile = validateInputFile(file);

        if (!validationFile.isValid) {
            setErrorTypeImageDescription(validationFile.message);
            return
        }

        const formData = new FormData()
        formData.append('image', file)
        formData.append('folder', 'image_description_with_html')


        try {
            const { data } = await imageUpload.uploadWithFolder(formData)
            setPathImageDescription(data?.path)
        } catch (error) {
        } finally {
            setIsUploadImage(false)
        }
    }

    function isValidTags() {
        if (!selectedTags || selectedTags.length <= 0) {
            setTagsError('min');
            return false;
        }

        if (selectedTags.length > 3) {
            setTagsError('max');
            return false;
        }

        return true;
    }

    function isValidAddress(address) {
        if (address?.notYet) {
            return true
        }

        if (!address) {
            return false
        }

        if (!address.id) {
            return false
        }

        return true
    }

    function createAddressPayload(address) {
        if (address?.notYet) {
            return null
        }

        const cleanedPostalcode = (address?.street?.postalcode || address?.postalcode)?.replace("-", "");


        if (address?.number) {
            return {
                postalcode: cleanedPostalcode,
                number: address?.number,
                complement: address?.complement
            }
        }

        if (address?.name) {
            return {
                postalcode: cleanedPostalcode,
                name: address?.name
            }
        }
    }

    function createPayload(data) {
        const isEstablishment = user?.role?.id == import.meta.env.VITE_ESTABLISHMENT_ROLE_ID;

        let payload = {
            ...event,
            dates: createDatesPayload(data),
            ...removeDuplicates(data),
            name: data?.name,
            description: description,
            description_with_html: descriptionWithHtml,
            address: createAddressPayload(event?.address),
            tags: selectedTags.map(el => el),
            draft: isDraft ? 1 : 0,
            hide_address: event?.hide_address,
            spotify_playlist_url: spotifyPlaylistUrl || null,
        }

        if (user?.establishments?.[0]?.id) {
            payload = {
                ...payload,
                establishment_id: user.establishments[0].id,
            }
        }

        if (!payload.link_ticket) {
            delete payload.link_ticket
        }

        if (!payload.link) {
            delete payload.link
        }

        if (!payload?.discount) {
            delete payload.discount
        }

        if (!payload?.discount_type) {
            delete payload.discount_type
        }

        if (!payload?.images?.length) {
            delete payload.images
        }

        if (payload?.images) {
            delete payload.images

        }

        delete payload.spotify_embed_url
        delete payload.spotify_playlist_id

        if (edit) {
            if (payload?.image_id) {
                delete payload?.image_id
            }

            if (imageId) {
                payload.images_id = [imageId]
            }

            if (!imageId) {
                payload.images_id = []
            }

        }

        return payload
    }

    function isValidDescription() {
        return description && description != '\n';
    }

    function validateCustomFields() {
        const validTags = isValidTags();
        const validDescription = isValidDescription();

        setDescriptionError(!validDescription);

        return validTags && validDescription;
    }

    function onInvalidSubmit() {
        validateCustomFields();
    }

    async function editEvent(content) {
        if (!validateCustomFields()) {
            return;
        }

        onLoading(true);

        const payload = createPayload(content);

        const withTickets = event?.has_tickets;

        try {
            setTagsError(false);
            setRequestError(false);

            const { data } = await eventsApi.update(edit.slug, payload);
            setEvent(data)
            onSuccess({ ...data, has_tickets: withTickets }, 'edit')
        } catch (error) {
            setRequestError('Ocorreu um erro inesperado.')
            onError(true)
        } finally {
            onLoading(false)
        }
    }

    async function createEvent(content) {
        if (!validateCustomFields()) {
            return;
        }

        onLoading(true);

        const payload = createPayload(content);
        const withTickets = event?.has_tickets;

        try {
            setTagsError(false);
            setRequestError(false);

            const { data } = await eventsApi.create(payload);
            const formatedData = {
                ...data,
                has_tickets: withTickets
            }
            setEvent(formatedData)
            onSuccess(formatedData, 'create')
        } catch ({ response }) {
            if (response.status == 409) {
                setRequestError('Esse evento já existe.');
                onError(true);
                return
            }
            setRequestError('Ocorreu um erro inesperado.')
            onError(true)
        } finally {
            onLoading(false)
        }
    }


    return (
        <FormContainer onSubmit={handleSubmit(edit ? editEvent : createEvent, onInvalidSubmit)}>
            {requestError ? (
                <ErrorBanner>
                    <AlertCircle size={20} color={colors.danger} />
                    <Text color={colors.danger} type='bold'>
                        {requestError}
                    </Text>
                    <RetryLink onClick={() => setRequestError(false)}>
                        Tentar novamente
                    </RetryLink>
                </ErrorBanner>
            ) : null}

            <FormHeader>
                <Text name='title' color={colors.nightDark}>
                    {edit ? 'Atualizar' : 'Criar'} {isDraft ? 'rascunho' : 'evento'}
                </Text>
                <Text color={colors.night}>
                    {edit
                        ? 'Atualize as informações do seu evento abaixo.'
                        : 'Preencha as informações abaixo para criar seu evento.'
                    }
                </Text>
            </FormHeader>

            <FormSection>
                <BasicInfos
                    unregister={unregister}
                    register={register}
                    errors={errors}
                    edit={edit}
                    reset={reset}
                    setValue={setValue}
                    getValues={getValues}
                    descriptionError={descriptionError}
                    onChangeDescription={e => { setDescription(e); setDescriptionError(false); }}
                    onChangeDescriptionWithHtml={e => setDescriptionWithHtml(e)}
                    uploadImageDescription={e => uploadImageDescription(e)}
                    errorTypeImageDescription={errorTypeImageDescription}
                    setErrorTypeImageDescription={setErrorTypeImageDescription}
                    pathImageDescription={pathImageDescription}
                />
            </FormSection>

            <FormSection>
                <ImageInfo
                    isEdit={edit}
                    onToggleImage={(e) => uploadImage(e)}
                    onDeleteFile={() => onToggleDelete()}
                    errorImage={errorImage}
                    setErrorImage={setErrorImage}
                    setErrorTypeImage={setErrorTypeImage}
                    errorTypeImage={errorTypeImage}
                    allowedFiles={allowedFilesTypes.images}
                />
            </FormSection>

            <FormSection>
                <CategoriesInfo
                    error={tagsError}
                    edit={edit?.tags}
                    tagsLoading={tagsLoading}
                    setSelecteds={(tags) => { setSelectedTags(tags); setTagsError(false); }}
                />
            </FormSection>

            <FormSection>
                <AddressInfo trigger={trigger} edit={edit} />
            </FormSection>

            <FormSection>
                <ExtraInfo
                    register={register}
                    errors={errors}
                    edit={edit}
                    onChangeSpotifyUrl={(url) => setSpotifyPlaylistUrl(url)}
                />
            </FormSection>

            <SubmitArea>
                <SubmitButton
                    type="submit"
                    disabled={isUploadImage}
                    onClick={() => setTrigger(true)}
                >
                    {edit ? buttonText.update : buttonText.next}
                    <ArrowRight size={16} />
                </SubmitButton>
            </SubmitArea>
        </FormContainer>
    )
};

const FormContainer = styled.form`
    width: 100%;
    padding: 0 4px 40px 4px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 10px 40px 10px;
    }
`;

const FormHeader = styled.div`
    margin-bottom: 28px;

    & > h1 {
        margin-bottom: 4px;
    }
`;

const FormSection = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px 28px;
    }
`;

const ErrorBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    background: #FEF2F2;
    border: 1px solid ${colors.dangerLight};
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 20px;
`;

const RetryLink = styled.span`
    color: ${colors.primary};
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    margin-left: auto;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

const SubmitArea = styled.div`
    margin-top: 8px;
    display: flex;
    justify-content: center;
`;

const SubmitButton = styled.button.withConfig({
    shouldForwardProp: (prop) => prop !== 'disabled',
})`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    max-width: 280px;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '18' : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.night + '55' : '#fff'};
    font-size: 14px;
    font-weight: 700;
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    transition: all 0.15s ease;

    ${({ disabled }) => !disabled && `
        &:hover {
            background: ${colors.secondaryPurple};
        }
    `};
`;
